import { createSelector, createStructuredSelector } from "reselect"
import {
    selectSearchingColumnIndexes,
    selectSearchingColumnValues,
    selectSheetCanFitInMemory,
    selectSheetIsLoading,
    selectCanHideColumns,
} from "sheets/selectors/sheet-selectors"

import { selectResourceTableDatumSearchTrigger } from "sheets/components/cells/cell-selectors/datum-cell-selectors"

import {
    selectCanRenameColumns,
    selectColumnDataForColumn,
    selectColumnDataType,
    selectColumnField,
    selectColumnForComponent,
    selectColumnRange,
    selectColumnType,
    selectCustomTag,
    selectFieldObjectForSearch,
    selectDisplayKeyForEnumColumn,
    selectPropFromProps,
    selectResourceTableSearchTrigger,
} from "sheets/selectors/column-selectors"
import { selectAlignmentTypesAsEnum } from "shared/stakeholder/selectors"
import { searchTriggerForColumnType } from "sheets/components/columns/columnSearchComponents"
import { dayMonthYearToMoment } from "shared/imports/sharedHelperFunctions"
import moment from "moment"

import sheetConstants from "sheets/constants"
import * as helperFunctions from "sheets/helper-functions"

// select the searching column value for this component, only grabbing the value
// if this component is the searching column index
// TODO: investigate if its possible to return "" instead of undefined, as our FormControl doesn't play nice with undefined
export const selectSearchingColumnValuesForTrigger = createSelector(
    selectSearchingColumnIndexes,
    selectSearchingColumnValues,
    selectPropFromProps("columnIndex"),
    (searchingColumnIndexes, searchingColumnValues, columnIndex) => {
        if (!searchingColumnIndexes || !searchingColumnValues) {
            return undefined
        }

        if (columnIndex === null || columnIndex === undefined) {
            return undefined
        }

        if (searchingColumnIndexes.includes(columnIndex)) {
            return searchingColumnValues.get(String(columnIndex))
        }

        return undefined
    }
)

// if the column has an enumFilter in the column configuration, select it
export const selectOptionalEnumFilter = createSelector(selectPropFromProps("column"), (column) => {
    if (column && column.enumFilter && typeof column.enumFilter === "function") {
        return column.enumFilter
    } else {
        return (_) => true
    }
})

export const selectOptionsForColumnEnumSelect = createSelector(
    selectPropFromProps("field"),
    selectPropFromProps("columnData"),
    selectOptionalEnumFilter,
    selectPropFromProps("canFitInMemory"),
    (field, columnData, optionalEnumFilter, hasAllColumnData) =>
        helperFunctions
            .generateRelevantOptions(field, columnData, hasAllColumnData)
            .filter(optionalEnumFilter)
            .sort(DjangIO.valueSort)
)

export const selectSearchTrigger = createSelector(
    selectColumnType,
    selectColumnDataType,
    selectFieldObjectForSearch,
    selectCustomTag,
    selectResourceTableSearchTrigger,
    selectAlignmentTypesAsEnum,
    selectResourceTableDatumSearchTrigger,
    (columnType, columnDataType, fieldObj, customTag, searchTrigger, alignmentTypes, datumSearchTrigger) =>
        datumSearchTrigger ||
        searchTrigger ||
        searchTriggerForColumnType(columnType, columnDataType, fieldObj, customTag, alignmentTypes)
)

// get the start date for the Date Range component
export const selectStartDate = createSelector(
    selectPropFromProps("value"),
    selectPropFromProps("columnData"),
    selectPropFromProps("shouldUseColumnDataForInitialValues"),
    (value, columnData, shouldUseColumnDataForInitialValues) => {
        // if we are already searching and we need to rerender the component
        if (value && value.startDate) {
            return dayMonthYearToMoment(value.startDate)
        }
        if (shouldUseColumnDataForInitialValues && columnData) {
            return moment(columnData.sort((a, b) => (moment(a) > moment(b) ? -1 : 1)).last())
        } else {
            return null
        }
    }
)

// get the end date for the Date Range component
export const selectEndDate = createSelector(
    selectPropFromProps("value"),
    selectPropFromProps("columnData"),
    selectPropFromProps("shouldUseColumnDataForInitialValues"),
    (value, columnData, shouldUseColumnDataForInitialValues) => {
        // if we are already searching and we need to rerender the component
        if (value && value.endDate) {
            return dayMonthYearToMoment(value.endDate)
        }
        if (shouldUseColumnDataForInitialValues && columnData) {
            return moment(columnData.sort((a, b) => (moment(a) > moment(b) ? -1 : 1)).first())
        } else {
            return null
        }
    }
)

export const selectIsDatumColumnSearching = createSelector(
    selectSearchingColumnIndexes,
    (searchingColumnIndexes) => searchingColumnIndexes === sheetConstants.DATUM_COLUMN_INDEX
)

// ------------- BEGIN Connections ------- //
export const columnEnumFieldConnection = createStructuredSelector({
    optionalEnumFilter: (state, props) => selectOptionalEnumFilter(state, props),
    overrideLabel: (state, props) => selectDisplayKeyForEnumColumn(state, props),
    filteredOptions: (state, props) => selectOptionsForColumnEnumSelect(state, props),
})

export const columnSearchTriggerConnection = createStructuredSelector({
    canFitInMemory: selectSheetCanFitInMemory,
    canHideColumns: selectCanHideColumns,
    canRenameColumns: selectCanRenameColumns,
    column: selectColumnForComponent,
    columnData: selectColumnDataForColumn,
    columnDataType: selectColumnDataType,
    columnField: selectColumnField,
    columnRange: selectColumnRange,
    columnType: selectColumnType,
    customTag: selectCustomTag,
    field: selectFieldObjectForSearch,
    loading: selectSheetIsLoading,
    searching: selectIsDatumColumnSearching,
    searchingColumnIndexes: selectSearchingColumnIndexes,
    searchingColumnValues: selectSearchingColumnValuesForTrigger,
    SearchTrigger: selectSearchTrigger,
    value: selectSearchingColumnValuesForTrigger,
})
// -------------END CONNECTIONS ------- //

export const columnSearchDateRangeConnection = createStructuredSelector({
    startDate: selectStartDate,
    endDate: selectEndDate,
})
